import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { PMDialog } from "../../utils/PMDialog";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import {
  CalendarIcon,
  PlusIcon,
  TrashIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";
import { createJournal, updateJournal } from "../../../api/journal";
import { PMField } from "../../utils/PMField";
import PMCombobox from "../../utils/PMCombobox";

const JournalDialog = ({
  open,
  onClose,
  moduleType,
  moduleParam,
  moduleRecord = null,
  journal = null,
  onJournalCreated,
  onJournalUpdated,
}) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [inputFields, setInputFields] = useState([]);
  const [showNewFieldForm, setShowNewFieldForm] = useState(false);
  const [newField, setNewField] = useState({ name: "", value: "" });

  // Default input fields from the Ruby model
  const defaultInputFields = [
    { name: "name", value: "name" },
    { name: "email", value: "email" },
    { name: "mobile", value: "mobile" },
    { name: "address", value: "address" },
    { name: "address2", value: "address2" },
    { name: "postal", value: "postal" },
    { name: "city", value: "city" },
    { name: "model_name", value: "model_name" },
    { name: "model_year", value: "model_year" },
    { name: "serial_number", value: "serial_number" },
  ];

  // Journal status options
  const statusOptions = [
    { value: "active", title: t("journal.status.active") },
    { value: "completed", title: t("journal.status.completed") },
    { value: "archived", title: t("journal.status.archived") },
  ];

  useEffect(() => {
    if (journal && journal.input_fields) {
      setInputFields(journal.input_fields);
    }
  }, [journal]);

  const handleAddInputField = () => {
    if (!newField.name.trim() || !newField.value.trim()) {
      toast.error(t("Both name and value are required"));
      return;
    }

    setInputFields([...inputFields, { ...newField, id: Date.now() }]);
    setNewField({ name: "", value: "" });
    setShowNewFieldForm(false);
  };

  const handleRemoveInputField = (index) => {
    setInputFields(inputFields.filter((_, i) => i !== index));
  };

  const handleSelectDefaultField = (field) => {
    const exists = inputFields.find((f) => f.name === field.name);
    if (exists) {
      toast.error(t("Field already added"));
      return;
    }

    setInputFields([...inputFields, { ...field, id: Date.now(), value: "" }]);
  };

  const initialValues = {
    title: journal?.title || "",
    description: journal?.description || "",
    journal_date:
      journal?.journal_date || new Date().toISOString().split("T")[0],
    next_follow_up_date: journal?.next_follow_up_date || "",
    status: journal?.status || "active",
  };

  const validationSchema = Yup.object({
    title: Yup.string().required(t("journal.title") + " " + t("is required")),
    description: Yup.string().required(
      t("journal.description") + " " + t("is required")
    ),
    journal_date: Yup.date().required(
      t("journal.date") + " " + t("is required")
    ),
  });

  const handleSubmit = async (values, helpers) => {
    setIsLoading(true);
    try {
      const payload = {
        ...values,
        module_type: moduleType,
        module_param: moduleParam,
        module_record_type: moduleRecord?.type,
        module_record_id: moduleRecord?.id,
        input_fields_attributes: inputFields.map((field) => ({
          name: field.name,
          value: field.value,
        })),
      };

      let result;
      if (journal) {
        result = await updateJournal(journal.id, payload);
        toast.success(t("journal.entry_updated"));
        onJournalUpdated?.(result.data);
      } else {
        result = await createJournal(payload);
        toast.success(t("journal.entry_created"));
        onJournalCreated?.(result.data);
      }

      onClose();
    } catch (error) {
      console.error("Error saving journal:", error);
      toast.error(t("Failed to save journal entry"));
    } finally {
      setIsLoading(false);
      helpers.setSubmitting(false);
    }
  };

  return (
    <PMDialog
      open={open}
      onClose={onClose}
      title={journal ? t("journal.edit_entry") : t("journal.add_entry")}
      size="2xl"
    >
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {(formik) => (
          <Form className="space-y-6">
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <PMField
                name="title"
                label={t("journal.title")}
                type="text"
                required
              />

              <PMField
                name="journal_date"
                label={t("journal.date")}
                type="date"
                required
              />

              <PMField
                name="next_follow_up_date"
                label={t("journal.next_follow_up")}
                type="date"
              />

              <PMCombobox
                name="status"
                label={t("journal.status")}
                options={statusOptions}
                {...formik}
              />
            </div>

            <PMField
              name="description"
              label={t("journal.description")}
              type="textarea"
              rows={4}
              required
            />

            {/* Input Fields Section */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {t("Input Fields")}
              </h3>

              {/* Default Fields Selector */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("Add Default Field")}
                </label>
                <select
                  onChange={(e) => {
                    const selectedField = defaultInputFields.find(
                      (f) => f.name === e.target.value
                    );
                    if (selectedField) {
                      handleSelectDefaultField(selectedField);
                    }
                  }}
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  defaultValue=""
                >
                  <option value="">{t("Select a default field...")}</option>
                  {defaultInputFields.map((field) => (
                    <option key={field.name} value={field.name}>
                      {t(field.name)}
                    </option>
                  ))}
                </select>
              </div>

              {/* New Field Form */}
              <div className="mb-4">
                <button
                  type="button"
                  onClick={() => setShowNewFieldForm(!showNewFieldForm)}
                  className="flex items-center px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  {t("Add Custom Field")}
                </button>

                {showNewFieldForm && (
                  <div className="mt-3 p-3 bg-white rounded-lg border">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          {t("Field Name")}
                        </label>
                        <input
                          type="text"
                          value={newField.name}
                          onChange={(e) =>
                            setNewField({
                              ...newField,
                              name: e.target.value,
                            })
                          }
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                          placeholder={t("Enter field name")}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          {t("Field Value")}
                        </label>
                        <input
                          type="text"
                          value={newField.value}
                          onChange={(e) =>
                            setNewField({
                              ...newField,
                              value: e.target.value,
                            })
                          }
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                          placeholder={t("Enter field value")}
                        />
                      </div>
                    </div>
                    <div className="mt-3 flex space-x-2">
                      <button
                        type="button"
                        onClick={handleAddInputField}
                        className="px-3 py-1 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        {t("Add Field")}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowNewFieldForm(false);
                          setNewField({ name: "", value: "" });
                        }}
                        className="px-3 py-1 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                      >
                        {t("Cancel")}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Input Fields Table */}
              {inputFields.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">
                    {t("Added Fields")}
                  </h4>
                  <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                    <table className="min-w-full divide-y divide-gray-300">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                            {t("Name")}
                          </th>
                          <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                            {t("Value")}
                          </th>
                          <th className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                            <span className="sr-only">{t("Actions")}</span>
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 bg-white">
                        {inputFields.map((field, index) => (
                          <tr key={field.id}>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900">
                              {t(field.name)}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                              <input
                                type="text"
                                value={field.value}
                                onChange={(e) => {
                                  const updatedFields = [...inputFields];
                                  updatedFields[index].value = e.target.value;
                                  setInputFields(updatedFields);
                                }}
                                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm"
                                placeholder={t("Enter value")}
                              />
                            </td>
                            <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                              <button
                                type="button"
                                onClick={() => handleRemoveInputField(index)}
                                className="text-red-600 hover:text-red-900"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                {t("Cancel")}
              </button>
              <button
                type="submit"
                disabled={isLoading || formik.isSubmitting}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {isLoading || formik.isSubmitting
                  ? journal
                    ? t("Updating...")
                    : t("Creating...")
                  : journal
                  ? t("Update Journal")
                  : t("Create Journal")}
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </PMDialog>
  );
};

export default JournalDialog;
